import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Trophy, Clock, Sparkles, Flame, CalendarCheck } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { calculateXpForLevel, calculateXpForNextLevel } from '@/lib/xpUtils';

export function LevelAndFocusCard({ onLevelUp }: { onLevelUp?: (level: number, title: string) => void }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [xp, setXp] = useState(0);
  const [level, setLevel] = useState(1);
  const [levelTitle, setLevelTitle] = useState('Bookworm Beginner');
  const [focusMinutes, setFocusMinutes] = useState(0);
  const [focusStreak, setFocusStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [daysActiveThisWeek, setDaysActiveThisWeek] = useState(0);
  const [weeklyGoal, setWeeklyGoal] = useState(7);
  const [loading, setLoading] = useState(true);

  const DAILY_GOAL_MINUTES = 15;

  useEffect(() => {
    if (!user) return;
    
    loadData();
    
    // Subscribe to real-time XP updates
    const channel = supabase
      .channel('xp-updates')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'profiles',
        filter: `id=eq.${user.id}`
      }, (payload) => {
        const oldLevel = level;
        const newXp = (payload.new as any).xp_points;
        const newLevel = (payload.new as any).level;
        const newTitle = (payload.new as any).level_title;
        
        setXp(newXp);
        setLevel(newLevel);
        setLevelTitle(newTitle || 'Bookworm Beginner');

        if (newLevel > oldLevel) {
          toast({
            title: "🎉 Level Up!",
            description: `You're now Level ${newLevel} - ${newTitle}!`,
            duration: 5000,
          });
          
          // Trigger celebration
          if (onLevelUp) {
            onLevelUp(newLevel, newTitle);
          }
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, level]);

  const loadData = async () => {
    try {
      // Load XP data
      const { data: profileData } = await supabase
        .from('profiles')
        .select('xp_points, level, level_title')
        .eq('id', user!.id)
        .single();

      if (profileData) {
        setXp(profileData.xp_points || 0);
        setLevel(profileData.level || 1);
        setLevelTitle(profileData.level_title || 'Bookworm Beginner');
      }

      // Load today's focus sessions
      const today = new Date().toISOString().split('T')[0];
      const { data: focusSessions } = await supabase
        .from('focus_sessions')
        .select('actual_duration_seconds')
        .eq('user_id', user!.id)
        .gte('created_at', `${today}T00:00:00`)
        .lte('created_at', `${today}T23:59:59`)
        .eq('was_completed', true);

      if (focusSessions) {
        const totalSeconds = focusSessions.reduce(
          (sum, session) => sum + (session.actual_duration_seconds || 0),
          0
        );
        setFocusMinutes(Math.floor(totalSeconds / 60));
      }

      // Load focus streak data
      const { data: streakData } = await supabase
        .from('focus_streaks')
        .select('current_streak, longest_streak')
        .eq('user_id', user!.id)
        .maybeSingle();

      if (streakData) {
        setFocusStreak(streakData.current_streak || 0);
        setLongestStreak(streakData.longest_streak || 0);
      }

      // Load weekly activity data
      const { data: weeklyData } = await supabase
        .from('user_streaks')
        .select('week_activity_days, weekly_goal_days')
        .eq('user_id', user!.id)
        .maybeSingle();

      if (weeklyData) {
        const weekActivityDays = weeklyData.week_activity_days;
        setDaysActiveThisWeek(Array.isArray(weekActivityDays) ? weekActivityDays.length : 0);
        setWeeklyGoal(weeklyData.weekly_goal_days || 7);
      }
    } catch (error) {
      console.error('Error loading level and focus data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="card-seamless">
        <CardContent className="p-6">
          <div className="h-40 bg-muted animate-pulse rounded" />
        </CardContent>
      </Card>
    );
  }

  const xpForCurrentLevel = calculateXpForLevel(level);
  const xpForNextLevel = calculateXpForLevel(level + 1);
  const xpNeededForNextLevel = calculateXpForNextLevel(level);
  const xpProgressPercent = Math.min(((xp - xpForCurrentLevel) / xpNeededForNextLevel) * 100, 100);
  const focusProgressPercent = Math.min((focusMinutes / DAILY_GOAL_MINUTES) * 100, 100);

  return (
    <Card className="card-seamless group">
      <CardContent className="p-4 space-y-3">
        {/* Level Progress */}
        <div 
          className="cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('/achievements')}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-yellow-100 to-orange-100 dark:from-yellow-900/30 dark:to-orange-900/30">
              <Trophy className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-bold">Level {level}</p>
                <Sparkles className="w-3 h-3 text-yellow-500" />
              </div>
              <p className="text-xs text-muted-foreground">{levelTitle}</p>
            </div>
          </div>
          <Progress value={xpProgressPercent} className="h-2 mb-1" />
          <p className="text-xs text-muted-foreground">
            {xp} / {xpForNextLevel} XP
          </p>
        </div>

        {/* Divider */}
        <div className="border-t border-border" />

        {/* Daily Reading Goal */}
        <div 
          className="cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('/focus')}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-primary/10 to-primary/20 dark:from-primary/20 dark:to-primary/30">
              <Clock className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold">
                Daily Reading Goal
              </p>
              <p className="text-xs text-muted-foreground">
                {focusMinutes} / {DAILY_GOAL_MINUTES} minutes
              </p>
            </div>
          </div>
          <Progress 
            value={focusProgressPercent} 
            className="h-2 mb-1" 
          />
          <p className="text-xs text-muted-foreground">
            {focusMinutes >= DAILY_GOAL_MINUTES 
              ? '🎉 Goal complete!' 
              : `${DAILY_GOAL_MINUTES - focusMinutes} min remaining`
            }
          </p>
        </div>

        {/* Divider */}
        <div className="border-t border-border" />

        {/* Focus Streak */}
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/30 dark:to-red-900/30">
            <Flame className="w-5 h-5 text-orange-500" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold">
              {focusStreak} Day Streak
            </p>
            <p className="text-xs text-muted-foreground">
              {focusStreak === 0 ? 'Complete a session to start!' : `Best: ${longestStreak} days`}
            </p>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-border" />

        {/* Weekly Activity */}
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30">
            <CalendarCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold">
              {daysActiveThisWeek}/{weeklyGoal} Days This Week
            </p>
            <p className="text-xs text-muted-foreground">
              {daysActiveThisWeek >= weeklyGoal 
                ? '🎉 Weekly goal complete!' 
                : `${weeklyGoal - daysActiveThisWeek} more to go`
              }
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
